import React from "react"
import { type ApolloError } from "@apollo/client"
import {
  type Transaction,
  useMatchTransactionsMutation,
} from "@digits-graphql/frontend/graphql-bearer"
import { SvgAlertTriangleSolid } from "@digits-shared/components/SVGIcons/solid/AlertTriangleSolid.svg"
import { SvgCheckCircleSolid } from "@digits-shared/components/SVGIcons/solid/CheckCircleSolid.svg"
import useSession from "@digits-shared/hooks/useSession"
import { v4 as generateUUID } from "uuid"
import { useTransactionsCanBeMatched } from "src/frontend/components/OS/Applications/Shared/Accounting/Matching/hooks/useTransactionsCanBeMatched"
import { useChromeAlertContext } from "src/frontend/components/Shared/Alerts/ChromeAlertsContext"
import type FrontendSession from "src/frontend/session"

/*
  INTERFACES
*/

interface Props {
  transactions: Transaction[]
  onMatch?: (transactions: Transaction[]) => void
  onError?: (error: ApolloError) => void
}

/*
  HOOKS
*/

// Validates transactions can be matched and calls the mutation
// to match them. If successful, it will call the onMatch callback
export const useMatchTransactions = ({ transactions, onMatch, onError }: Props) => {
  const { currentLegalEntityId: legalEntityId } = useSession<FrontendSession>()
  const { valid, reason } = useTransactionsCanBeMatched(transactions)

  const ledgerTransactionIds = React.useMemo(
    () => transactions.map((transaction) => transaction.ledgerTransactionId),
    [transactions]
  )
  const [matchTransactions] = useMatchTransactionsMutation({
    variables: {
      legalEntityId,
      ledgerTransactionIds,
    },
  })
  const { addAlert } = useChromeAlertContext()

  return React.useCallback(() => {
    if (!valid)
      return addAlert({
        id: generateUUID(),
        message: "Cannot match transaction",
        description: reason,
        autoDismiss: true,
        SVGComponent: SvgAlertTriangleSolid,
      })

    return matchTransactions()
      .then(() => {
        addAlert({
          id: generateUUID(),
          message: "Transactions matched",
          description: "Updates may take a few minutes to reflect",
          autoDismiss: true,
          SVGComponent: SvgCheckCircleSolid,
        })
        onMatch?.(transactions)
      })
      .catch((err) => {
        addAlert({
          id: generateUUID(),
          message: "Error matching transactions",
          autoDismiss: true,
          SVGComponent: SvgAlertTriangleSolid,
        })
        onError?.(err)
      })
  }, [addAlert, matchTransactions, onError, onMatch, reason, transactions, valid])
}
